import { useForm, UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useUpsertEntity } from 'src/data-capture/merge-review/useUpsertEntities';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { DynamicUiData, FormValuesType } from 'src/UI/dynamicUI/types';
import { useSidebarMenu } from 'src/UI/page-nav/MenuTriggerSlot';
import useDynamicNonFinancialData from './useDynamicNonFinancialData';
import { prepFormValuesForUpsert } from '../prepFormValuesForUpsert';
import { buildInitialValues } from '../components/utils';
import { EntityFormContextType } from '../EntityFormProvider';

type UseEntitySectionForm = {
  form: UseFormReturnType<FormValuesType>;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  toggleEditing: () => void;
  handleSubmit: (formValues: FormValuesType) => Promise<void>;
  contextValue: EntityFormContextType | null;
  nonFinancialData: DynamicUiData | undefined;
  isLoading: boolean;
  isError: boolean;
  isInitialized: boolean;
};

export function useEntitySectionForm(): UseEntitySectionForm {
  useSidebarMenu({ customTrigger: true });
  const { clientId } = useCurrentClient();
  const {
    nonFinancialData,
    isLoading,
    isError,
    refetch: refetchEntities,
  } = useDynamicNonFinancialData();
  const { upsertEntities } = useUpsertEntity();
  const [isEditing, setIsEditing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: {},
  });

  useEffect(() => {
    if ((!isInitialized || !isEditing) && !isLoading && nonFinancialData) {
      const initialValues = buildInitialValues(nonFinancialData);
      form.setInitialValues(initialValues);
      form.setValues(initialValues);
      setIsInitialized(true);
    }
  }, [isLoading, nonFinancialData]);

  const handleSubmit = async (formValues: FormValuesType) => {
    if (!nonFinancialData) return;
    try {
      const valuesToSubmit = prepFormValuesForUpsert(formValues, nonFinancialData, form, false);
      await upsertEntities(clientId, valuesToSubmit, false);
      await refetchEntities();
      form.resetDirty();
      setIsEditing(false);
    } catch (e) {
      notifyAndLogError(
        'Failed to save changes',
        'Something went wrong while saving your changes. Please refresh the page and try again. If the issue persists, please contact support',
        e,
        { formValues },
      );
    }
  };

  const toggleEditing = () => {
    if (isEditing) {
      form.reset();
    }
    setIsEditing(!isEditing);
  };

  const contextValue: EntityFormContextType | null = nonFinancialData
    ? {
        form,
        isEditing,
        setIsEditing,
        entities: nonFinancialData.entities,
        entitiesMetadata: nonFinancialData.ui.entitiesMetadata,
        clientId,
      }
    : null;

  return {
    form,
    isEditing,
    setIsEditing,
    toggleEditing,
    handleSubmit,
    contextValue,
    nonFinancialData,
    isLoading,
    isError,
    isInitialized,
  };
}
